'use client'

import { useConsent, toggleOneTrustInfoDisplay } from '@marketplace-web/consent/consent-feature'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { clickEvent } from '@marketplace-web/footer/footer-data'
import { CookieConsentVersion } from '@marketplace-web/consent/consent-data'

import {
  BUSINESS_TERMS_OF_SALE_URL,
  BUSINESS_TERMS_URL,
  COOKIE_POLICY_URL,
  OUR_PLATFORM_URL,
  PRIVACY_POLICY_URL,
  BUSINESS_TERMS_AND_CONDITIONS_URL,
  TERMS_URL,
} from '../../constants/routes'
import SectionLink from './SectionLink'

type Props = {
  isDigitalLawEnabled: boolean
  isBusinessInfoLinksEnabled: boolean
  onlineComplaintUrl: string | undefined
}

const PrivacySection = ({
  isDigitalLawEnabled,
  isBusinessInfoLinksEnabled,
  onlineComplaintUrl,
}: Props) => {
  const translate = useTranslate('footer.policy.items')
  const { track } = useTracking()

  const { cookieConsentVersion, isCookieConsentVersion } = useConsent()

  const isProTermsAndConditionsFSEnabled = useFeatureSwitch('pro_terms_and_conditions_enabled')

  const handleCookieSettingsClick = () => {
    toggleOneTrustInfoDisplay()
    track(
      clickEvent({
        target: 'cookie_settings',
        screen: 'main_footer',
      }),
    )
  }

  const cookieSettingsText =
    cookieConsentVersion === CookieConsentVersion.California
      ? translate('cookie_settings_us')
      : translate('cookie_settings')

  const renderBusinessInfoLinks = () => {
    if (!isBusinessInfoLinksEnabled) return null

    if (isProTermsAndConditionsFSEnabled) {
      return (
        <SectionLink
          text={translate('pro_terms_and_conditions')}
          url={BUSINESS_TERMS_AND_CONDITIONS_URL}
          testId="privacy-footer-pro-terms-and-conditions-link"
        />
      )
    }

    return (
      <>
        <SectionLink
          text={translate('pro_terms_of_sale')}
          url={BUSINESS_TERMS_OF_SALE_URL}
          testId="privacy-footer-pro-sale-term-link"
        />
        <SectionLink
          text={translate('pro_terms_of_use')}
          url={BUSINESS_TERMS_URL}
          testId="privacy-footer-pro-use-term-link"
        />
      </>
    )
  }

  return (
    <ul className="main-footer__privacy-section">
      <SectionLink text={translate('privacy_policy')} url={PRIVACY_POLICY_URL} />
      <SectionLink text={translate('cookie_policy')} url={COOKIE_POLICY_URL} />

      {isCookieConsentVersion && (
        <li className="main-footer__privacy-section-item">
          <button
            className="main-footer__privacy-section-link"
            type="button"
            onClick={handleCookieSettingsClick}
          >
            {cookieSettingsText}
          </button>
        </li>
      )}
      <SectionLink
        text={translate('terms_and_conditions')}
        url={TERMS_URL}
        testId="privacy-footer-terms-and-conditions-link"
      />
      {isDigitalLawEnabled && (
        <SectionLink text={translate('our_platform')} url={OUR_PLATFORM_URL} />
      )}
      {renderBusinessInfoLinks()}
      {onlineComplaintUrl && (
        <SectionLink text={translate('online_complaints')} url={onlineComplaintUrl} />
      )}
    </ul>
  )
}

export default PrivacySection
